<script setup lang="ts">
import { computed, useSlots } from "vue";
import { RouterLink, type RouteLocationRaw } from "vue-router";

type ButtonSize = "medium" | "small";
type ButtonVariant = "ghost" | "primary" | "secondary";

interface Properties {
  disabled?: boolean;
  size?: ButtonSize;
  to?: RouteLocationRaw;
  type?: "button" | "submit" | "reset";
  variant?: ButtonVariant;
}

const properties = withDefaults(defineProps<Properties>(), {
  disabled: false,
  size: "medium",
  to: undefined,
  type: "button",
  variant: "primary",
});

defineEmits<{
  click: [event: MouseEvent];
}>();

const slots = useSlots();

const hasPrefix = computed(() => Boolean(slots.prefix));
const hasSuffix = computed(() => Boolean(slots.suffix));

const variantClasses: Record<ButtonVariant, string> = {
  ghost:
    "bg-transparent text-app-text-muted hover:bg-app-surface-raised hover:text-app-text focus-visible:outline-app-border-strong",
  primary:
    "bg-app-accent text-app-accent-text shadow-app-soft hover:bg-app-accent-strong focus-visible:outline-app-accent",
  secondary:
    "bg-app-surface text-app-text shadow-app-soft ring-1 ring-app-border-strong backdrop-blur-md hover:bg-app-surface-raised focus-visible:outline-app-border-strong",
};

const sizeClasses: Record<ButtonSize, string> = {
  medium: "px-5 py-2.5 text-base",
  small: "px-3 py-1.5 text-sm",
};

const buttonClasses = computed(() => [
  "inline-flex items-center rounded-2xl font-semibold transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
  sizeClasses[properties.size],
  variantClasses[properties.variant],
]);

const prefixClasses = computed(() => (properties.size === "small" ? "pr-2" : "pr-3"));
const suffixClasses = computed(() => (properties.size === "small" ? "pl-2" : "pl-3"));
</script>

<template>
  <RouterLink v-if="to !== undefined" :class="buttonClasses" :to="to">
    <span v-if="hasPrefix" :class="['hidden shrink-0 items-center md:flex', prefixClasses]">
      <slot name="prefix" />
    </span>
    <span class="flex flex-1 items-center justify-center text-center">
      <slot />
    </span>
    <span v-if="hasSuffix" :class="['hidden shrink-0 items-center md:flex', suffixClasses]">
      <slot name="suffix" />
    </span>
  </RouterLink>
  <button v-else :class="buttonClasses" :disabled="disabled" :type="type" @click="$emit('click', $event)">
    <span v-if="hasPrefix" :class="['hidden shrink-0 items-center md:flex', prefixClasses]">
      <slot name="prefix" />
    </span>
    <span class="flex flex-1 items-center justify-center text-center">
      <slot />
    </span>
    <span v-if="hasSuffix" :class="['hidden shrink-0 items-center md:flex', suffixClasses]">
      <slot name="suffix" />
    </span>
  </button>
</template>
